import { api } from "./api.js";
// Type-only, and one direction: `projects.ts` imports `api.js`, `staff.js` and
// `projectPortfolio.js` and never this module, so there is no cycle to worry
// about. `createProjectFromEstimate` below lives here rather than there because
// the endpoint is a sales one and this file is where the estimate ACTIONS are
// grouped - the thing it hands back just happens to be a project.
import type { Project } from "./projects.js";

// ── Types ───────────────────────────────────────────────────────────────────
export interface LineItem {
  itemId?: string;
  /** The line's name - one short label, scanned down a column. */
  description: string;
  /**
   * The paragraph under the name, several lines of it if need be. Perfex keeps
   * the same two texts side by side ("Item" and "Description"), and they are
   * read differently: the name is compared line to line, the paragraph is read
   * once. Plain text, newlines kept as typed.
   */
  details?: string;
  qty: number;
  unit?: string;
  unitPriceCents: number;
  taxRatePct: number;
  discountPct?: number;
}

export interface Item {
  id: string;
  name: string;
  description: string | null;
  unitPriceCents: number;
  unit: string | null;
  taxRatePct: number;
  sku: string | null;
  active: boolean;
  group: string | null;
  createdAt: string;
}

export interface DocClient {
  id: string;
  name: string;
  kind: "company" | "person";
  paymentTermsDays?: number | null;
}

/** How a quantity is shown on the document (Perfex "Show quantity as"). */
export type QtyMode = "qty" | "hours" | "qty_hours";

export type ProposalStatus = "draft" | "sent" | "accepted" | "declined";
export type EstimateStatus = "draft" | "sent" | "accepted" | "declined" | "expired";
export type InvoiceStatus = "draft" | "sent" | "partially_paid" | "paid" | "overdue" | "cancelled";
export type CreditNoteStatus = "draft" | "issued" | "applied";

interface DocBase {
  id: string;
  number: string;
  clientId: string;
  client?: DocClient;
  lineItems: LineItem[];
  qtyMode: QtyMode;
  subtotalCents: number;
  taxCents: number;
  totalCents: number;
  currency: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Proposal extends DocBase {
  title: string | null;
  status: ProposalStatus;
  validUntil: string | null;
  sentAt: string | null;
  acceptedAt: string | null;
  /**
   * The client's own answer, given on the public link. Null on every proposal
   * answered at this desk, for the same reason `Estimate.portalDecision` is:
   * `status` alone cannot tell "the client said yes" from "somebody here typed
   * it in after a telephone call".
   */
  portalDecision: EstimatePortalDecision | null;
  convertedEstimateId: string | null;
}

export interface Estimate extends DocBase {
  title: string | null;
  status: EstimateStatus;
  /**
   * The project this document belongs to, or null.
   *
   * A loose id, not a relation: there is no foreign key behind `projectId` in
   * the CRM schema, so the only thing keeping a document off another client's
   * project is the `project_client_mismatch` check the write routes run. Which
   * is also why every form that offers this field has to offer the projects of
   * the CHOSEN client and nobody else's.
   *
   * `Proposal` deliberately has no counterpart: the column does not exist on it.
   * A proposal reaches a project when it is converted, through the `projectId`
   * in the body of `POST /proposals/:id/convert-estimate`.
   */
  projectId: string | null;
  validUntil: string | null;
  sentAt: string | null;
  acceptedAt: string | null;
  convertedInvoiceId: string | null;
  /**
   * The client's own answer, given in the client portal. Null on every quote
   * answered at this desk, and that absence is the point.
   *
   * `status` and `acceptedAt` cannot tell the two apart: marking a quote
   * accepted in this screen writes exactly the same two columns the portal
   * does. So "did the client say yes themselves, or did somebody here type it
   * in after a phone call" has no answer anywhere else.
   */
  portalDecision: EstimatePortalDecision | null;
}

/** Who answered an offer, when, and from where. `via` says which door they
 *  came through: the signed-in portal, or the public link on the document. */
export interface EstimatePortalDecision {
  decision: "accepted" | "declined";
  via?: "portal" | "link";
  name?: string;
  email?: string;
  ip?: string;
  userAgent?: string;
  at?: string;
  note?: string;
}

export interface Payment {
  id: string;
  invoiceId: string;
  amountCents: number;
  currency: string;
  method: string | null;
  reference: string | null;
  paidAt: string;
  note: string | null;
}

export interface Invoice extends DocBase {
  title: string | null;
  status: InvoiceStatus;
  /** The project this invoice belongs to, or null. Same loose id as on
   *  `Estimate` - see the note there. */
  projectId: string | null;
  estimateId: string | null;
  discountCents: number;
  paidCents: number;
  issueDate: string;
  dueDate: string | null;
  terms: string | null;
  sentAt: string | null;
  paidAt: string | null;
  /**
   * When the client first opened this invoice in the portal, or null.
   *
   * Null is not "they have not seen it". The invoice mail attaches the PDF as
   * well as linking to the portal, so reading the attachment leaves this empty.
   * The screen therefore draws this only when it is SET - see
   * `PortalViewedCard`, which exists to keep that rule in one place.
   */
  viewedAt: string | null;
  payments?: Payment[];
  creditNotes?: { id: string; number: string }[];
}

export interface CreditNote extends DocBase {
  status: CreditNoteStatus;
  invoiceId: string | null;
  invoice?: { id: string; number: string } | null;
  reason: string | null;
  issuedAt: string | null;
}

export interface Expense {
  id: string;
  clientId: string | null;
  client?: { id: string; name: string } | null;
  /**
   * The project this spend is booked against, or null.
   *
   * The same loose id as on `Estimate`, with one difference that matters at the
   * call site: the expense routes take `projectId` but run NO client check, so
   * here the scoped picker in the form is the only thing standing between the
   * operator and a cost landing in another client's project total.
   */
  projectId: string | null;
  category: string;
  vendor: string | null;
  description: string | null;
  amountCents: number;
  taxCents: number;
  currency: string;
  spentAt: string;
  billable: boolean;
  invoicedId: string | null;
  receiptUrl: string | null;
  createdAt: string;
}

export interface SalesStats {
  revenueMonthCents: number;
  revenueYearCents: number;
  outstandingCents: number;
  outstandingCount: number;
  overdueCents: number;
  overdueCount: number;
  draftInvoiceCount: number;
  openEstimateCount: number;
}

interface Page<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

function qs(params?: Record<string, string | number | undefined>): string {
  if (!params) return "";
  const p = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return p.length ? `?${p.join("&")}` : "";
}

// ── Catalogue items (the line-item templates) ───────────────────────────────
export const getItems = (params?: Record<string, string | number | undefined>) =>
  api.get<{ rows: Item[]; total: number }>(`/admin/crm/items${qs(params)}`);
export const createItem = (body: Partial<Item>) => api.post<Item>("/admin/crm/items", body);
export const updateItem = (id: string, body: Partial<Item>) => api.patch<Item>(`/admin/crm/items/${id}`, body);
export const deleteItem = (id: string) => api.del<{ ok: boolean }>(`/admin/crm/items/${id}`);

// ── Proposals ───────────────────────────────────────────────────────────────
export const getProposals = (params?: Record<string, string | number | undefined>) =>
  api.get<Page<Proposal>>(`/admin/crm/proposals${qs(params)}`);
export const getProposal = (id: string) => api.get<Proposal>(`/admin/crm/proposals/${id}`);
export const createProposal = (body: Record<string, unknown>) => api.post<Proposal>("/admin/crm/proposals", body);
export const updateProposal = (id: string, body: Record<string, unknown>) => api.patch<Proposal>(`/admin/crm/proposals/${id}`, body);
export const deleteProposal = (id: string) => api.del<{ ok: boolean }>(`/admin/crm/proposals/${id}`);
export const sendProposal = (id: string) => api.post<Proposal>(`/admin/crm/proposals/${id}/send`, {});
export const proposalToEstimate = (id: string) => api.post<{ ok: boolean; estimate: Estimate }>(`/admin/crm/proposals/${id}/convert-estimate`, {});

// ── Estimates ───────────────────────────────────────────────────────────────
export const getEstimates = (params?: Record<string, string | number | undefined>) =>
  api.get<Page<Estimate>>(`/admin/crm/estimates${qs(params)}`);
export const getEstimate = (id: string) => api.get<Estimate>(`/admin/crm/estimates/${id}`);
export const createEstimate = (body: Record<string, unknown>) => api.post<Estimate>("/admin/crm/estimates", body);
export const updateEstimate = (id: string, body: Record<string, unknown>) => api.patch<Estimate>(`/admin/crm/estimates/${id}`, body);
export const deleteEstimate = (id: string) => api.del<{ ok: boolean }>(`/admin/crm/estimates/${id}`);
export const sendEstimate = (id: string) => api.post<Estimate>(`/admin/crm/estimates/${id}/send`, {});
/** The letter the mail composer opens with, the estimate's PDF already staged on it. */
export interface DocMailDraft {
  to: string | null;
  subject: string;
  body: string;
  attachment: { id: string; name: string; size: number };
}
export const getEstimateMailDraft = (id: string) => api.post<DocMailDraft>(`/admin/crm/estimates/${id}/mail-draft`, {});
/** Stamp the estimate sent after the mailbox letter carrying it went out. */
export const markEstimateSent = (id: string) => api.post<Estimate>(`/admin/crm/estimates/${id}/mark-sent`, {});
export const estimateToInvoice = (id: string) => api.post<{ ok: boolean; invoice: Invoice }>(`/admin/crm/estimates/${id}/convert-invoice`, {});
/**
 * Start the work a quote was won for: a Project is created beside the estimate,
 * carrying the amount the client agreed to. Unlike `estimateToInvoice` this
 * converts nothing - the estimate stays exactly as it is and gains a
 * `projectId`.
 *
 * `name` is optional and falls back on the server to the estimate's title and
 * then to its number, so a caller may simply send `{}`; there is no such thing
 * as a nameless project here.
 *
 * The budget it sets is the NET total (`subtotalCents`), not the gross one. Every
 * cost on the other side of a budget comparison is net, so copying the VAT-bearing
 * figure would make each project read roughly a quarter further from its ceiling
 * than it is. A screen that shows the operator what the budget will be must show
 * the same number.
 *
 * Guarded by `manage:crm.projects`, NOT `manage:crm.sales` like the rest of this
 * file. A button gated on the sales right renders for people who then get a 403.
 *
 * One refusal: 409 `{ error: "already_has_project", projectId }` when the estimate
 * has been started already - by an earlier click, by a second operator racing this
 * one, or by a link written by hand through PATCH. The `projectId` in that body is
 * re-read from the winner, so it is where the operator should be sent; it is
 * `null` only if the link vanished between the failure and the re-read.
 */
export const createProjectFromEstimate = (id: string, body: { name?: string }) =>
  api.post<{ ok: boolean; project: Project }>(`/admin/crm/estimates/${id}/create-project`, body);

// ── Invoices ────────────────────────────────────────────────────────────────
export const getInvoices = (params?: Record<string, string | number | undefined>) =>
  api.get<Page<Invoice>>(`/admin/crm/invoices${qs(params)}`);
export const getInvoice = (id: string) => api.get<Invoice>(`/admin/crm/invoices/${id}`);
export const createInvoice = (body: Record<string, unknown>) => api.post<Invoice>("/admin/crm/invoices", body);
export const updateInvoice = (id: string, body: Record<string, unknown>) => api.patch<Invoice>(`/admin/crm/invoices/${id}`, body);
export const deleteInvoice = (id: string) => api.del<{ ok: boolean }>(`/admin/crm/invoices/${id}`);
export const sendInvoice = (id: string) => api.post<Invoice>(`/admin/crm/invoices/${id}/send`, {});
export const markInvoicePaid = (id: string) => api.post<Invoice>(`/admin/crm/invoices/${id}/mark-paid`, {});
export const addPayment = (
  id: string,
  body: { amountCents: number; method?: string; reference?: string; paidAt?: string; note?: string },
) =>
  api.post<Payment>(`/admin/crm/invoices/${id}/payments`, body);
/** Correct a payment already booked. Every field optional - only what moved is sent. */
export const updatePayment = (
  id: string,
  pid: string,
  body: { amountCents?: number; method?: string | null; reference?: string | null; paidAt?: string; note?: string | null },
) => api.patch<Payment>(`/admin/crm/invoices/${id}/payments/${pid}`, body);
export const deletePayment = (id: string, pid: string) => api.del<{ ok: boolean }>(`/admin/crm/invoices/${id}/payments/${pid}`);

// ── Credit notes ────────────────────────────────────────────────────────────
export const getCreditNotes = (params?: Record<string, string | number | undefined>) =>
  api.get<Page<CreditNote>>(`/admin/crm/credit-notes${qs(params)}`);
export const getCreditNote = (id: string) => api.get<CreditNote>(`/admin/crm/credit-notes/${id}`);
export const createCreditNote = (body: Record<string, unknown>) => api.post<CreditNote>("/admin/crm/credit-notes", body);
export const updateCreditNote = (id: string, body: Record<string, unknown>) => api.patch<CreditNote>(`/admin/crm/credit-notes/${id}`, body);
export const deleteCreditNote = (id: string) => api.del<{ ok: boolean }>(`/admin/crm/credit-notes/${id}`);

// ── Expenses ────────────────────────────────────────────────────────────────
export const getExpenses = (params?: Record<string, string | number | undefined>) =>
  api.get<Page<Expense> & { sumCents: number; taxSumCents: number }>(`/admin/crm/expenses${qs(params)}`);
export const getExpenseCategories = () => api.get<{ rows: string[] }>("/admin/crm/expense-categories");
export const createExpense = (body: Record<string, unknown>) => api.post<Expense>("/admin/crm/expenses", body);
export const updateExpense = (id: string, body: Record<string, unknown>) => api.patch<Expense>(`/admin/crm/expenses/${id}`, body);
export const deleteExpense = (id: string) => api.del<{ ok: boolean }>(`/admin/crm/expenses/${id}`);
export const billExpense = (id: string) => api.post<{ ok: boolean; invoice: Invoice }>(`/admin/crm/expenses/${id}/bill`, {});

// ── Stats ───────────────────────────────────────────────────────────────────
export const getSalesStats = () => api.get<SalesStats>("/admin/crm/sales/stats");

// ── The public link, and the thread under a document ────────────────────────
/**
 * The three kinds a public link exists for. Singular, because that is what the
 * API's path says: the CRM's own `DocKind` is plural and includes credit notes,
 * which are not shared this way.
 */
export type SalesShareType = "invoice" | "estimate" | "proposal";

export interface PublicDocLink {
  token: string | null;
  url: string | null;
  /** Whether a client following the link would see anything. A draft would not. */
  visible: boolean;
}

const linkPath = (type: SalesShareType, id: string) => `/admin/crm/sales/${type}/${id}/link`;

/** Read-only: opening a document must never mint a public address for it. */
export const getPublicDocLink = (type: SalesShareType, id: string) =>
  api.get<PublicDocLink>(linkPath(type, id));
export const createPublicDocLink = (type: SalesShareType, id: string) =>
  api.post<PublicDocLink>(linkPath(type, id));
export const rotatePublicDocLink = (type: SalesShareType, id: string) =>
  api.post<PublicDocLink>(`${linkPath(type, id)}/rotate`);
export const revokePublicDocLink = (type: SalesShareType, id: string) =>
  api.del<PublicDocLink>(linkPath(type, id));

export interface DocComment {
  id: string;
  /** `client` (written on the public page) or `staff` (written here). */
  authorKind: string;
  authorName: string;
  body: string;
  createdAt: string;
}

const commentsPath = (type: SalesShareType, id: string) => `/admin/crm/sales/${type}/${id}/comments`;

export const listDocComments = (type: SalesShareType, id: string) =>
  api.get<DocComment[]>(commentsPath(type, id));
export const addDocComment = (type: SalesShareType, id: string, body: string) =>
  api.post<DocComment>(commentsPath(type, id), { body });
/** Take one line out of the thread, ours or the client's. It leaves the public
 *  page in the same move - that page IS the thread. */
export const deleteDocComment = (type: SalesShareType, id: string, commentId: string) =>
  api.del<{ ok: true }>(`${commentsPath(type, id)}/${commentId}`);

/** One recorded opening of a document. */
export interface DocView {
  id: string;
  /** `link` (the public page), `pdf` (the file) or `portal` (a signed-in client). */
  source: string;
  createdAt: string;
}

/**
 * Every opening of one document, newest first.
 *
 * `total` is the true count and `views` may be shorter than it: the API caps
 * what it sends back, so a document opened two hundred times shows the recent
 * ones and counts them all.
 */
export interface DocViewHistory {
  views: DocView[];
  total: number;
  firstAt: string | null;
  lastAt: string | null;
}

export const listDocViews = (type: SalesShareType, id: string) =>
  api.get<DocViewHistory>(`/admin/crm/sales/${type}/${id}/views`);

/** The letter the one-click send would mail, shown for confirmation first. */
export interface SalesSendPreview {
  /** Null when the sending channel is set up so that nothing would leave. */
  from: string | null;
  to: string | null;
  subject: string;
  html: string;
  attachment: string;
  /** The mailbox adds a signature on the way out. */
  signed: boolean;
  /** Set when the document will be marked sent without any mail leaving. */
  reason: "no_recipient" | "disabled" | "no_host_site" | null;
}

export const getSendPreview = (type: SalesShareType, id: string) =>
  api.get<SalesSendPreview>(`/admin/crm/sales/${type}/${id}/send-preview`);

// ── Company details (the sender on a PDF or an e-mail) ──────────────────────
export type DocColorMode = "color" | "bw";
/** What the QR code at the foot of a document says. Mirrors the API's
 *  `crm-sales/lib/qr/plan.ts`: `auto` pays where it can and links everywhere
 *  else, `link` never prints a payment code, `off` prints none. */
export type DocQrMode = "auto" | "link" | "off";
export interface CompanyInfo {
  name: string;
  regCode: string;
  vatNumber: string;
  email: string;
  phone: string;
  website: string;
  address: string;
  iban: string;
  bankName: string;
  footerNote: string;
  logoUrl: string;
  colorMode: DocColorMode;
  accentColor: string;
  qrMode: DocQrMode;
}
export const getCompany = () => api.get<CompanyInfo>("/admin/crm/company");
export const saveCompany = (body: Partial<CompanyInfo>) => api.put<CompanyInfo>("/admin/crm/company", body);

// ── Document numbering settings ─────────────────────────────────────────────
export type DocNumberType = "invoice" | "estimate" | "proposal" | "creditnote";
export interface NumberingConfig {
  templates: Record<DocNumberType, string>;
  /**
   * The invoice term. Kept beside `dueDaysByType` because the server still
   * sends it: it is what every reader written before the per-type split asked
   * for, and all of those readers dated an invoice.
   */
  dueDays: number;
  /** The payment term per document kind. The server fills every kind. */
  dueDaysByType: Record<DocNumberType, number>;
  /** VAT applied to lines this CRM writes today. Historical documents keep the rate they were issued at. */
  defaultTaxRatePct: number;
  /** Is the company registered for VAT. Off means new work is written without any. */
  vatEnabled: boolean;
  /** The language a new document is raised in when its client has none of their own. */
  docLocale: string;
  /**
   * The languages the document PDF can PRINT, served rather than duplicated
   * here: the list is a property of the renderer's font, not of this screen.
   * Absent on a bundle talking to an older API - see `FALLBACK_DOC_LOCALES`.
   */
  docLocales?: string[];
  /**
   * What a new line actually starts at: the rate above, or zero while VAT is
   * switched off. Read this to fill a field; read `defaultTaxRatePct` only on
   * the settings screen, which is where the stored rate is edited.
   */
  effectiveTaxRatePct: number;
  sequences: Record<DocNumberType, { year: number; lastNumber: number; next: number }>;
}
export interface NumberingPatch {
  templates?: Partial<Record<DocNumberType, string>>;
  /** The pre-split single term. The server still accepts it and applies it to the invoice. */
  dueDays?: number;
  dueDaysByType?: Partial<Record<DocNumberType, number>>;
  defaultTaxRatePct?: number;
  vatEnabled?: boolean;
  docLocale?: string;
  next?: Partial<Record<DocNumberType, number>>;
}
export const getNumbering = () => api.get<NumberingConfig>("/admin/crm/numbering");
export const saveNumbering = (body: NumberingPatch) => api.put<NumberingConfig>("/admin/crm/numbering", body);

/** Preview - format a sample number from the template (mirrors the server's `formatNumber`). */
export function previewNumber(template: string, n: number): string {
  const d = new Date();
  return (template || "{YYYY}-{NNNN}")
    .replace(/\{YYYY\}/g, String(d.getFullYear()))
    .replace(/\{YY\}/g, String(d.getFullYear()).slice(-2))
    .replace(/\{MM\}/g, String(d.getMonth() + 1).padStart(2, "0"))
    .replace(/\{DD\}/g, String(d.getDate()).padStart(2, "0"))
    .replace(/\{NNNNN\}/g, String(n).padStart(5, "0"))
    .replace(/\{NNNN\}/g, String(n).padStart(4, "0"))
    .replace(/\{NNN\}/g, String(n).padStart(3, "0"))
    .replace(/\{N\}/g, String(n));
}

/** The send result `/send` returns alongside the document. */
export interface EmailMeta {
  status: "sent" | "failed" | "dev" | null;
  reason: "not_found" | "no_recipient" | "no_host_site" | null;
}

/**
 * Download the document's PDF (authenticated - a fetch with the session cookie
 * plus a blob, not window.open). `kind` is the route segment (e.g. "invoices",
 * "credit-notes").
 */
export async function downloadDocPdf(kind: string, id: string, filename: string): Promise<void> {
  const res = await fetch(`/api/admin/crm/${kind}/${id}/pdf`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Helpers ─────────────────────────────────────────────────────────────────
export function formatCents(cents: number, currency = "EUR"): string {
  return new Intl.NumberFormat("et-EE", { style: "currency", currency }).format(cents / 100);
}

export function computeLineTotals(items: LineItem[]) {
  let subtotalCents = 0;
  let taxCents = 0;
  for (const li of items) {
    const gross = Math.round((li.qty || 0) * (li.unitPriceCents || 0));
    const discount = li.discountPct ? Math.round((gross * li.discountPct) / 100) : 0;
    const net = gross - discount;
    subtotalCents += net;
    taxCents += Math.round((net * (li.taxRatePct ?? 0)) / 100);
  }
  return { subtotalCents, taxCents, totalCents: subtotalCents + taxCents };
}
